import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiService from "services/apiService";
import GenericModal from "components/Modal";
import AddLeave from "../leaves/AddLeave/AddLeave";
import { useToaster } from "contexts/toasterContext";
import {
  Send,
  ArrowRight,
  RefreshCw,
  CalendarDays,
  CheckCircle2,
  Clock,
  Wallet,
  Heart,
  Sun,
  Briefcase,
  CalendarCheck2,
  Info,
  type LucideIcon,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────
   LeaveSummary — the "Leave" tab on the employee Profile page.

   A compact "Leave Overview" dashboard (the detailed applied-leave list lives
   on the dedicated Leaves page). It shows: summary counts, per-policy balance
   cards, and quick apply actions. All data loads only when this tab mounts.

   APIs:
   • GET api/leave/data/{year}?page=0&size=200&email={email} → summary counts
   • GET api/leave-balance/me?month&year                     → balance cards
   • GET api/leave-policies                                  → optional enrichment
   ──────────────────────────────────────────────────────────────────────── */

// ── Types ─────────────────────────────────────────────────────────────────
interface LeaveRecord {
  id: number;
  leaveType: string;
  leaveFrom: string;
  leaveTo: string;
  unit: number;
  status: string;
  reason: string;
  year: number;
  month: string;
  email: string;
}

interface LeaveBalance {
  id: number;
  userId: number;
  leavePolicyId: number;
  leaveType: string;
  balance: number;
  lastAccrualDate: string;
  month: number;
  year: number;
  // Per-type usage for the current period (added by the balance API).
  approvedCount?: number;
  pendingCount?: number;
}

interface LeavePolicy {
  id: number;
  leaveType: string;
  daysPerYear: number;
  isAccrual: boolean;
  maxCarryForward: number;
}

interface SummaryCounts {
  applied: number;
  approved: number;
  pending: number;
}

interface ProfileUser {
  email?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const APPLIED_LEAVES_PATH = "/admin/leaves";

// Clean day formatting: "1", "8", "0.5", "1.5" (trailing zeros trimmed).
const formatDays = (value: number): string => String(parseFloat((Number(value) || 0).toFixed(2)));

// Day count with a correctly pluralised unit: "1 day", "2 days", "0.5 days".
const daysLabel = (value: number): string => {
  const n = Number(value) || 0;
  return `${formatDays(n)} ${n === 1 ? "day" : "days"}`;
};

const formatDate = (value?: string): string => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// Per-type icon + pastel accent (semantic, falls back to a neutral accent).
const typeVisual = (leaveType: string): { icon: LucideIcon; accent: string; bar: string } => {
  const t = (leaveType || "").toLowerCase();
  if (t.includes("sick") || t.includes("medical"))
    return { icon: Heart, accent: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400", bar: "bg-rose-500" };
  if (t.includes("casual"))
    return { icon: Sun, accent: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400", bar: "bg-amber-500" };
  if (t.includes("annual") || t.includes("paid"))
    return { icon: CalendarDays, accent: "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400", bar: "bg-blue-500" };
  if (t.includes("other"))
    return { icon: Briefcase, accent: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400", bar: "bg-violet-500" };
  return { icon: CalendarCheck2, accent: "bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400", bar: "bg-teal-500" };
};

// One labelled key→value row inside a balance card. Keeping the label and the
// value on the same line (instead of a column grid) makes the unit unambiguous:
// "Approved requests  1" reads very differently from a bare "1" under "Approved".
export const StatRow: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({
  label,
  value,
  tone,
}) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${tone ?? "text-slate-800 dark:text-slate-100"}`}>
      {value}
    </span>
  </div>
);

// ── Small presentational pieces ─────────────────────────────────────────────
const SummaryCard: React.FC<{
  icon: LucideIcon;
  tone: string;
  label: string;
  value: React.ReactNode;
  hint: string;
}> = ({ icon: Icon, tone, label, value, hint }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-gray-800">
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone}`}>
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <div className="min-w-0">
      <p className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
      <p className="text-xl font-bold leading-tight tabular-nums text-slate-900 dark:text-white">{value}</p>
      <p className="truncate text-[11px] text-slate-400 dark:text-slate-500">{hint}</p>
    </div>
  </div>
);

const InlineError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300">
    <span className="flex items-center gap-2">
      <Info className="h-4 w-4 shrink-0" /> {message}
    </span>
    <button
      type="button"
      onClick={onRetry}
      className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100 dark:border-rose-800 dark:bg-transparent dark:text-rose-300 dark:hover:bg-rose-900/40"
    >
      <RefreshCw className="h-3 w-3" /> Retry
    </button>
  </div>
);

// ── Main component ───────────────────────────────────────────────────────────
const LeaveSummary: React.FC<{ user?: ProfileUser | null }> = ({ user }) => {
  const { addToast } = useToaster();
  const email = user?.email ?? "";

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based for the balance API

  const [year, setYear] = useState<number>(currentYear);

  const [counts, setCounts] = useState<SummaryCounts>({ applied: 0, approved: 0, pending: 0 });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [countsError, setCountsError] = useState(false);

  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [policies, setPolicies] = useState<LeavePolicy[]>([]);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [balanceError, setBalanceError] = useState(false);

  // Apply Leave modal — optional preselected type from a balance card.
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyType, setApplyType] = useState<string | undefined>(undefined);

  const yearOptions = useMemo(
    () => Array.from({ length: 5 }, (_, i) => currentYear - i),
    [currentYear],
  );

  const totalAvailable = useMemo(
    () => balances.reduce((sum, b) => sum + (Number(b.balance) || 0), 0),
    [balances],
  );

  // Summary counts — fetch the year's applied leaves once and split by status
  // client-side. `applied` uses totalElements (accurate); approved/pending are
  // counted from the returned records (a large page size covers a user's year).
  const fetchCounts = useCallback(() => {
    if (!email) {
      setLoadingCounts(false);
      return;
    }
    setLoadingCounts(true);
    setCountsError(false);

    apiService
      .get(`api/leave/data/${year}?page=0&size=200&email=${encodeURIComponent(email)}`)
      .then((r) => {
        const data = r?.data as { content?: LeaveRecord[]; totalElements?: number } | undefined;
        const content = Array.isArray(data?.content) ? (data?.content as LeaveRecord[]) : [];
        const norm = (s?: string) => (s || "").toLowerCase();
        const approved = content.filter((x) => norm(x.status) === "approved").length;
        const pending = content.filter((x) => ["submitted", "pending"].includes(norm(x.status))).length;
        const applied = typeof data?.totalElements === "number" ? data.totalElements : content.length;
        setCounts({ applied, approved, pending });
      })
      .catch((error: { response?: { status?: number } }) => {
        // 404 (no records) is a valid empty result, not an error.
        if (error?.response?.status === 404) {
          setCounts({ applied: 0, approved: 0, pending: 0 });
        } else {
          setCountsError(true);
        }
      })
      .finally(() => setLoadingCounts(false));
  }, [email, year]);

  // Balance (current month, selected year) + optional policy enrichment.
  const fetchBalance = useCallback(() => {
    setLoadingBalance(true);
    setBalanceError(false);
    Promise.all([
      apiService.get(`api/leave-balance/me?month=${currentMonth}&year=${year}`).then((r) =>
        Array.isArray(r.data) ? (r.data as LeaveBalance[]) : [],
      ),
      // Enrichment only — regular users may lack access; ignore failures.
      apiService
        .get("api/leave-policies")
        .then((r) => {
          const data = r.data as LeavePolicy[] | { content?: LeavePolicy[] };
          return Array.isArray(data) ? data : data?.content ?? [];
        })
        .catch(() => [] as LeavePolicy[]),
    ])
      .then(([balanceData, policyData]) => {
        setBalances(balanceData);
        setPolicies(policyData);
      })
      .catch(() => setBalanceError(true))
      .finally(() => setLoadingBalance(false));
  }, [currentMonth, year]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const policyFor = (b: LeaveBalance): LeavePolicy | undefined =>
    policies.find((p) => p.id === b.leavePolicyId) ||
    policies.find((p) => p.leaveType?.toLowerCase() === b.leaveType?.toLowerCase());

  const openApply = (leaveType?: string) => {
    setApplyType(leaveType);
    setApplyOpen(true);
  };

  const onApplied = () => {
    setApplyOpen(false);
    setApplyType(undefined);
    addToast("Leave applied successfully", "success");
    fetchCounts();
    fetchBalance();
  };

  return (
    <div className="w-full pb-6 pr-4">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Leave Overview</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Track your leave balance and manage your leave requests
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="leave-year">Year</label>
          <select
            id="leave-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-gray-700 dark:text-slate-100"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => openApply(undefined)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800"
          >
            <Send className="h-4 w-4" /> Apply Leave
          </button>
        </div>
      </div>

      {/* ── Summary counts (one row) ────────────────────────────────────── */}
      {countsError ? (
        <InlineError message="Unable to load leave summary." onRetry={fetchCounts} />
      ) : loadingCounts ? (
        <div className="grid grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[66px] animate-pulse rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          <SummaryCard
            icon={CalendarDays}
            tone="bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
            label="Total Applied"
            value={counts.applied}
            hint={`requests in ${year}`}
          />
          <SummaryCard
            icon={CheckCircle2}
            tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
            label="Total Approved"
            value={counts.approved}
            hint="approved requests"
          />
          <SummaryCard
            icon={Clock}
            tone="bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
            label="Total Pending"
            value={counts.pending}
            hint="pending requests"
          />
          <SummaryCard
            icon={Wallet}
            tone="bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400"
            label="Total Available"
            value={formatDays(totalAvailable)}
            hint="days across leave types"
          />
        </div>
      )}

      {/* ── Balance + Quick info ────────────────────────────────────────── */}
      <div className="mt-6 space-y-5">
        {/* Leave balance — full width, 3 cards per row on large screens */}
        <div>
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Leave Balance</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Your current leave balance for {year}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                {MONTH_LABELS[currentMonth - 1]} {year}
              </span>
              <button
                type="button"
                onClick={fetchBalance}
                title="Refresh balance"
                aria-label="Refresh balance"
                className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingBalance ? "animate-spin" : ""}`} />
              </button>
              <Link
                to={APPLIED_LEAVES_PATH}
                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                View Details <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>

          {balanceError ? (
            <InlineError message="Unable to load leave balance." onRetry={fetchBalance} />
          ) : loadingBalance ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[140px] animate-pulse rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800" />
              ))}
            </div>
          ) : balances.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-200 py-10 text-center dark:border-slate-700">
              <CalendarCheck2 className="h-7 w-7 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No leave policies available</p>
              <p className="max-w-xs text-xs text-slate-400 dark:text-slate-500">
                Your leave balance will appear here once a leave policy is assigned to you.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {balances.map((b) => {
                const { icon: Icon, accent } = typeVisual(b.leaveType);
                const policy = policyFor(b);
                const entitled = policy?.daysPerYear;
                const available = Number(b.balance) || 0;
                const depleted = available <= 0;
                // approvedCount / pendingCount are counts of leave *requests* for
                // the current period — not days — so they are labelled as such.
                const approvedCount = Number(b.approvedCount) || 0;
                const pendingCount = Number(b.pendingCount) || 0;
                // Sub-heading describes how this leave type is earned.
                const message = policy
                  ? policy.isAccrual
                    ? `Accrues ${daysLabel(policy.daysPerYear / 12)} every month`
                    : `${daysLabel(policy.daysPerYear)} granted per year`
                  : "Current leave balance";

                return (
                  <div
                    key={b.id}
                    className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-gray-800"
                  >
                    {/* Head: icon + type + availability badge */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${accent}`}>
                          <Icon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100" title={b.leaveType}>
                            {b.leaveType}
                          </p>
                          <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{message}</p>
                        </div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${depleted
                          ? "border-rose-200 bg-rose-50 text-rose-600 dark:border-rose-900/50 dark:bg-rose-900/30 dark:text-rose-400"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/30 dark:text-emerald-400"}`}
                      >
                        {depleted ? "No balance" : "Available"}
                      </span>
                    </div>

                    {/* Headline: the one number that matters — available balance in days. */}
                    <div className="mt-4 flex items-baseline gap-1.5">
                      <span className="text-3xl font-bold leading-none tabular-nums text-slate-900 dark:text-white">
                        {formatDays(available)}
                      </span>
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                        {available === 1 ? "day available" : "days available"}
                      </span>
                    </div>

                    {/* Readable, unit-explicit breakdown — one fact per row. */}
                    <div className="mt-3 divide-y divide-slate-100 border-y border-slate-100 dark:divide-slate-700/70 dark:border-slate-700/70">
                      <StatRow
                        label="Approved requests"
                        value={approvedCount}
                        tone="text-emerald-600 dark:text-emerald-400"
                      />
                      <StatRow
                        label="Pending requests"
                        value={pendingCount}
                        tone="text-amber-600 dark:text-amber-400"
                      />
                      {entitled != null && (
                        <StatRow label="Annual entitlement" value={daysLabel(entitled)} />
                      )}
                    </div>

                    {/* Footer: last update + apply */}
                    <div className="mt-3 flex items-center justify-between pt-0.5">
                      <span className="text-[11px] text-slate-400 dark:text-slate-500">
                        Updated {formatDate(b.lastAccrualDate)}
                      </span>
                      <button
                        type="button"
                        onClick={() => openApply(b.leaveType)}
                        disabled={depleted}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700 disabled:cursor-not-allowed disabled:text-slate-300 dark:text-blue-400 dark:hover:text-blue-300 dark:disabled:text-slate-600"
                      >
                        Apply Leave <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick info — compact full-width bar */}
        {/* <aside className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-gray-800">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                <Send className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Need to apply for leave?</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">Make a request in just a few clicks.</p>
              </div>
            </div>

            <ul className="flex flex-col gap-1.5 text-xs text-slate-500 dark:text-slate-400 sm:flex-row sm:flex-wrap sm:gap-x-5 lg:flex-1 lg:justify-center">
              <li className="flex gap-2"><span className="text-blue-500">•</span> Balances update periodically.</li>
              <li className="flex gap-2"><span className="text-blue-500">•</span> Approved leave is deducted from balance.</li>
              <li className="flex gap-2"><span className="text-blue-500">•</span> Contact HR if balance looks incorrect.</li>
            </ul>

            <button
              type="button"
              onClick={() => openApply(undefined)}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800"
            >
              <Send className="h-4 w-4" /> Apply Leave
            </button>
          </div>
        </aside> */}
      </div>

      {/* Apply Leave modal — reuses the existing AddLeave form. */}
      {applyOpen && (
        <GenericModal
          isOpen={applyOpen}
          onClose={() => setApplyOpen(false)}
          header="Apply for Leave"
          customWidth="700px"
        >
          <AddLeave
            defaultLeaveType={applyType}
            onSuccess={onApplied}
            onError={(msg?: string) => addToast(msg || "Failed to apply leave", "error")}
            onClose={() => setApplyOpen(false)}
          />
        </GenericModal>
      )}
    </div>
  );
};

export default LeaveSummary;
